/** Error + placeholder pages for the shell. */
import { Construction, ShieldAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '../components/ui/Button.js';
import { EmptyState } from '../components/ui/Feedback.js';
import { PageHeader } from '../components/ui/Layout.js';

export function Placeholder({ titleKey }: { titleKey: string }): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="grid gap-5">
      <PageHeader title={t(titleKey)} />
      <EmptyState
        icon={Construction}
        title={t('common.comingSoon')}
        hint={t('common.comingSoonHint')}
      />
    </div>
  );
}

function CenteredCard({
  code,
  title,
  message,
}: {
  code: string;
  title: ReactNode;
  message: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="grid min-h-screen place-items-center bg-bg px-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-surface p-8 text-center">
        <span className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-full bg-danger/15 text-danger">
          <ShieldAlert className="h-6 w-6" aria-hidden />
        </span>
        <p className="font-display text-5xl font-bold text-fg">{code}</p>
        <p className="mt-2 font-display text-lg font-semibold text-fg">{title}</p>
        <p className="mt-1 text-sm text-muted">{message}</p>
        <Link to="/dashboard" className="mt-5 inline-block">
          <Button variant="secondary">{t('common.back')}</Button>
        </Link>
      </div>
    </div>
  );
}

export function Forbidden(): ReactNode {
  const { t } = useTranslation();
  return (
    <CenteredCard code="403" title={t('errors.forbidden')} message={t('errors.forbiddenHint')} />
  );
}

export function NotFound(): ReactNode {
  const { t } = useTranslation();
  return (
    <CenteredCard code="404" title={t('errors.not_found')} message={t('errors.notFoundHint')} />
  );
}
